// The MIT License (MIT)
//
// Copyright (c) 2021 Camptocamp SA
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import angular from 'angular';

/**
 * Service that provides methods to get, insert, update and delete from items.
 *
 * @hidden
 */
export class FormService {
  /**
   * @param {angular.IHttpService} $http Angular http service.
   */
  constructor($http) {
    /**
     * @type {angular.IHttpService}
     * @private
     */
    this.http_ = $http;
  }

  /**
   * Build a query to fetch all items of a type
   * @param {string} type Type of item (policen, schaeden, vertraege).
   * @return {Promise}
   */
  getAllItems(type) {
    const url = `/${type}`;
    return this.http_.get(url);
  }

  /**
   * Build a query to get an item by the value of a property.
   * @param {string} item_value Value of the query.
   * @param {string} type Type of item (policen, schaeden, vertraege).
   * @param {string} property to query (fid, policennr, ...).
   * @return {Promise}
   */
  getItemById(item_value, type) {
    const url = `/${type}/${item_value}`;
    return this.http_.get(url);
  }

  /**
   * Build a query to fetch all items of a type attached to one given parzellen/gebaeude id.
   * @param {string} locationId the id to get the attached items from.
   * @param {string} type Type of item (policen, schaeden, vertraege).
   * @return {Promise}
   */
  getAllItemsForOneParzellen(locationId, type) {
    const url = `/${type}/by_location/${locationId}`;
    return this.http_.get(url);
  }

  /**
   * Build a query to delete an item.
   * @param {integer} item_id primary of the item to delete
   * @param {string} type Type of item (policen, schaeden, vertraege).
   * @return {Promise}
   */
  deleteItem(item_id, type) {
    const url = `/${type}/${item_id}`;
    return this.http_.delete(url);
  }

  /**
   * Build a query to create an item.
   * @param {Object} item the object to create
   * @param {string} type Type of item (policen, schaeden, vertraege).
   * @return {Promise}
   */
  createItem(item, type) {
    const url = `/${type}`;
    return this.http_.post(url, item.getValues());
  }

  /**
   * Build a query to update an item.
   * @param {Object} item the object to create
   * @param {string} type Type of item (policen, schaeden, vertraege)
   * @return {Promise}
   */
  updateItem(item, type) {
    const url = `/${type}/${item.getId()}`;
    return this.http_.put(url, item.getValues());
  }
}

FormService.$inject = ['$http'];

/**
 * @type {angular.IModule}
 * @hidden
 */
const myModule = angular.module('pnFormService', []);
myModule.service('pnFormService', FormService);

export default myModule;
